import { isFileSizeValid, useToast, useTranslations } from "@jugl-web/utils";
import { AttachmentType } from "@web-src/modules/chats/types";
import { useCallback } from "react";

export const useChatSelectFiles = ({
  filesCount,
  maxAttachments,
  acceptType,
}: {
  filesCount: number;
  maxAttachments: number;
  acceptType?: Partial<Record<AttachmentType, string>>;
}) => {
  const { toast } = useToast();
  const { t } = useTranslations();

  const showAttachmentsCountAlert = useCallback(() => {
    toast(
      t(
        {
          id: "chats-page.upload-attachments-count-alert",
          defaultMessage:
            "You can only upload {maxAttachments} {maxAttachments, plural, one {attachment} other {attachments}} per message",
        },
        {
          maxAttachments,
        }
      ),
      { variant: "error" }
    );
  }, [toast, t, maxAttachments]);

  const showAttachmentsSizeAlert = useCallback(
    (maxAllowedSize?: number) => {
      toast(
        t(
          {
            id: "chats-page.selected-file-is-too-big",
            defaultMessage:
              "Selected file is too large. The maximum size is {maxSize}MB",
          },
          { maxSize: maxAllowedSize || 0 }
        ),
        { variant: "error" }
      );
    },
    [toast, t]
  );

  const filterAttachmentsByType = useCallback(
    (attachments: File[], attachmentType: AttachmentType) =>
      attachments.filter(
        (attachment) => {
          const accept = acceptType?.[attachmentType];

          if (!accept) return true;

          const acceptItems = accept
            .split(",")
            .map((item) => item.trim().toLowerCase())
            .filter((item) => item.length > 0);

          const fileType = attachment.type.toLowerCase();
          const fileExtension = `.${attachment.name
            .split(".")
            .pop()
            ?.toLowerCase()}`;

          return acceptItems.some((acceptItem) => {
            if (acceptItem === "*") {
              return true;
            }

            if (acceptItem.startsWith(".")) {
              return fileExtension === acceptItem;
            }

            if (acceptItem.endsWith("/*")) {
              const baseType = acceptItem.split("/")[0];
              return fileType.startsWith(`${baseType}/`);
            }

            return fileType === acceptItem;
          });
        },
        [acceptType]
      ),
    [acceptType]
  );

  const filterAttachmentsBySize = useCallback(
    (attachments: File[], maxAllowedSize?: number) =>
      attachments.filter((attachment) => {
        if (typeof maxAllowedSize !== "number") return true;

        return isFileSizeValid(attachment, maxAllowedSize);
      }),
    []
  );

  const showIncorrectFilesAlert = useCallback(
    (attachmentType?: AttachmentType) => {
      if (!attachmentType) return;

      let toastMessage: string | undefined;

      switch (attachmentType) {
        case AttachmentType.audio:
          toastMessage = t({
            id: "chats-page.incorrect-file-format-upload-audio-only",
            defaultMessage:
              "Incorrect file format. Please upload files of Audio Format only",
          });
          break;
        case AttachmentType.video:
          toastMessage = t({
            id: "chats-page.incorrect-file-format-upload-video-only",
            defaultMessage:
              "Incorrect file format. Please upload files of Video Format only",
          });
          break;
        case AttachmentType.image:
          toastMessage = t({
            id: "chats-page.incorrect-file-format-upload-image-only",
            defaultMessage:
              "Incorrect file format. Please upload files of Image Format only",
          });
          break;
        case AttachmentType.file:
          toastMessage = t({
            id: "chats-page.incorrect-file-format-upload-file-only",
            defaultMessage:
              "Incorrect file format. Please upload files of File Format only",
          });
          break;
        default:
          break;
      }

      if (toastMessage) {
        toast(toastMessage, { variant: "error" });
      }
    },
    [toast, t]
  );

  const getCorrectUploadedFiles = useCallback(
    (
      uploadedFiles: File[],
      attachmentType: AttachmentType,
      maxAllowedSize?: number
    ) => {
      const filteredAttachmentsByType = filterAttachmentsByType(
        uploadedFiles,
        attachmentType
      );

      if (filteredAttachmentsByType.length !== uploadedFiles.length) {
        showIncorrectFilesAlert(attachmentType);
      }

      const filteredAttachmentsBySize = filterAttachmentsBySize(
        filteredAttachmentsByType,
        maxAllowedSize
      );

      if (
        typeof maxAllowedSize === "number" &&
        filteredAttachmentsBySize.length !== filteredAttachmentsByType.length
      ) {
        showAttachmentsSizeAlert(maxAllowedSize);
      }

      if (filesCount + filteredAttachmentsBySize.length > maxAttachments) {
        showAttachmentsCountAlert();
      }

      return filteredAttachmentsBySize.slice(0, maxAttachments - filesCount);
    },
    [
      filterAttachmentsByType,
      filterAttachmentsBySize,
      filesCount,
      maxAttachments,
      showIncorrectFilesAlert,
      showAttachmentsSizeAlert,
      showAttachmentsCountAlert,
    ]
  );

  return {
    getCorrectUploadedFiles,
  };
};
